import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { MainLayout } from '../layouts';
import { GET_RACE_ANALYSIS, GET_TRACK_CONDITIONS } from '@shared/graphql/queries';
import { formatDatePath, formatDateDisplay } from '../utils';

// Mock data for development
const mockRaceAnalysis = {
  track: {
    name: 'Caulfield',
    state: 'VIC',
    type: 'METROPOLITAN'
  },
  trackConditions: {
    weather: 'Fine',
    trackCondition: 'Good 4',
    penetrometer: 4.5,
    rail: 'True',
    comment: 'Perfect racing conditions'
  },
  races: [
    {
      race_id: 'race1',
      race_number: 1,
      race_name: 'Maiden Plate',
      distance: 1200,
      class: 'Maiden',
      race_time: '2025-03-15T03:15:00.000Z',
      age_restrictions: '3YO+',
      weight_type: 'Set Weights',
      prize_money: 50000,
      selections: {
        top: [
          {
            number: 1,
            horse_name: 'Fast Runner',
            jockey: 'J. Smith',
            trainer: 'T. Jones',
            weight: 58,
            barrier: 4,
            odds: 3.5,
            assessedOdds: 3.2,
            confidence: 85,
            analysis: {
              summary: 'Strong chance with good form',
              pros: 'Good barrier, strong jockey',
              cons: 'Carrying top weight'
            }
          },
          {
            number: 5,
            horse_name: 'Lucky Star',
            jockey: 'A. Johnson',
            trainer: 'M. Williams',
            weight: 56.5,
            barrier: 7,
            odds: 4.2,
            assessedOdds: 4.0,
            confidence: 75,
            analysis: {
              summary: 'Consistent performer',
              pros: 'Good recent form',
              cons: 'Wide barrier'
            }
          }
        ],
        value: [
          {
            number: 8,
            horse_name: 'Outsider',
            jockey: 'R. Brown',
            trainer: 'S. Davis',
            weight: 54,
            barrier: 2,
            odds: 12.0,
            assessedOdds: 8.0,
            confidence: 60,
            analysis: {
              summary: 'Good value at current odds',
              pros: 'Light weight, good barrier',
              cons: 'Inexperienced'
            }
          }
        ],
        speed_map: 'Fast Runner likely to lead from barrier 4, with Lucky Star settling midfield.',
        suggested_bets: [
          {
            type: 'Win',
            selection: 'Fast Runner',
            stake: '3 units'
          },
          {
            type: 'Each Way',
            selection: 'Outsider',
            stake: '1 unit'
          }
        ]
      }
    },
    {
      race_id: 'race2',
      race_number: 2,
      race_name: 'Benchmark 70',
      distance: 1600,
      class: 'BM70',
      race_time: '2025-03-15T04:00:00.000Z',
      age_restrictions: '4YO+',
      weight_type: 'Handicap',
      prize_money: 65000,
      selections: {
        top: [
          {
            number: 3,
            horse_name: 'Miler',
            jockey: 'P. Green',
            trainer: 'D. White',
            weight: 57.5,
            barrier: 5,
            odds: 2.8,
            assessedOdds: 2.5,
            confidence: 90,
            analysis: {
              summary: 'Strong chance at this distance',
              pros: 'Perfect distance, good form',
              cons: 'None significant'
            }
          }
        ]
      }
    }
  ],
  bestBets: {
    best: [
      {
        race: 1,
        number: 1,
        name: 'Fast Runner',
        confidence: 85,
        odds: 3.2,
        analysis: 'Strong chance with good form',
        stake: '3 units win'
      }
    ],
    value: {
      race: 1,
      number: 8,
      name: 'Outsider',
      confidence: 60,
      odds: 8.0,
      analysis: 'Good value at current odds',
      stake: '1 unit each way'
    }
  },
  quaddie: {
    legs: [
      { race: 5, selections: [1, 4, 7] },
      { race: 6, selections: [2, 5] },
      { race: 7, selections: [3, 8, 10] },
      { race: 8, selections: [1, 6] }
    ],
    cost: 36,
    comment: 'Confident in legs 1 and 3, banker in leg 2.'
  }
};

const RacePage: React.FC = () => {
  // Get parameters from URL
  const { dateString, trackName } = useParams<{ dateString: string; trackName: string }>();
  
  // Parse date from URL
  const parsedDate = dateString ? new Date(
    parseInt(dateString.substring(0, 4)),
    parseInt(dateString.substring(4, 6)) - 1,
    parseInt(dateString.substring(6, 8))
  ) : new Date();
  
  // State for selected race
  const [selectedRace, setSelectedRace] = useState<number>(1);
  
  // Query race analysis data
  const { loading, error, data } = useQuery(GET_RACE_ANALYSIS, {
    variables: { 
      date: parsedDate.toISOString().split('T')[0],
      trackName: trackName
    },
    skip: !dateString || !trackName
  });
  
  // Use mock data for development
  const raceAnalysis = data?.raceAnalysis || mockRaceAnalysis;
  
  // Get current race
  const currentRace = raceAnalysis?.races?.find(race => race.race_number === selectedRace) || raceAnalysis?.races?.[0];
  
  if (loading) {
    return (
      <MainLayout>
        <div className="p-4">
          <div className="bg-white p-6 rounded-lg shadow-card text-center">
            <p className="text-gray-500">Loading race analysis...</p>
          </div>
        </div>
      </MainLayout>
    );
  }
  
  if (error) {
    return (
      <MainLayout>
        <div className="p-4">
          <div className="bg-white p-6 rounded-lg shadow-card text-center">
            <p className="text-racing-red">Error loading race analysis: {error.message}</p>
            <Link to="/" className="mt-4 inline-block bg-racing-blue text-white px-4 py-2 rounded">
              Return to Home
            </Link>
          </div>
        </div>
      </MainLayout>
    );
  }
  
  return (
    <MainLayout>
      <div className="p-4">
        {/* Back to Meetings Link */}
        <div className="mb-4">
          <Link 
            to={`/${dateString}`} 
            className="text-racing-blue hover:text-racing-blue-dark"
          >
            ← Back to Meetings
          </Link>
        </div>
        
        {/* Track Header */}
        <div className="bg-white rounded-lg shadow-card mb-6">
          <div className="p-4 border-b border-racing-gray-darker">
            <h1 className="text-2xl font-bold">{trackName?.toUpperCase()} Race Analysis</h1>
            <p className="text-gray-600">{formatDateDisplay(parsedDate)}</p>
          </div>
          
          {/* Track Conditions */}
          {raceAnalysis?.trackConditions && (
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="font-semibold">Track: <span className="font-normal">{raceAnalysis.trackConditions.trackCondition} {raceAnalysis.trackConditions.penetrometer && `(${raceAnalysis.trackConditions.penetrometer})`}</span></p>
                <p className="font-semibold">Rail: <span className="font-normal">{raceAnalysis.trackConditions.rail}</span></p>
              </div>
              <div>
                <p className="font-semibold">Weather: <span className="font-normal">{raceAnalysis.trackConditions.weather}</span></p>
                {raceAnalysis.trackConditions.comment && (
                  <p className="font-semibold">Comment: <span className="font-normal">{raceAnalysis.trackConditions.comment}</span></p>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Race Navigation */}
        {raceAnalysis?.races && raceAnalysis.races.length > 0 && (
          <div className="bg-white rounded-lg shadow-card mb-6 p-4">
            <h2 className="text-lg font-bold mb-3">Race Navigation</h2>
            <div className="flex flex-wrap gap-2">
              {raceAnalysis.races.map(race => (
                <button
                  key={race.race_number}
                  className={`px-3 py-1 rounded ${selectedRace === race.race_number ? 'bg-racing-blue text-white' : 'bg-racing-gray hover:bg-racing-gray-dark'}`}
                  onClick={() => setSelectedRace(race.race_number)}
                >
                  Race {race.race_number}
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Best Bets Section */}
        {raceAnalysis?.bestBets && (
          <div className="bg-white rounded-lg shadow-card mb-6">
            <div className="bg-racing-blue text-white p-3 rounded-t-lg">
              <h2 className="text-lg font-bold">Best Bets</h2>
            </div>
            <div className="p-4">
              {raceAnalysis.bestBets.best && raceAnalysis.bestBets.best.length > 0 && (
                <div className="mb-4">
                  <h3 className="font-bold text-racing-blue mb-2">Top Selections</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {raceAnalysis.bestBets.best.map((bet, index) => (
                      <div key={index} className="border border-racing-gray-darker rounded p-3">
                        <div className="flex justify-between mb-2">
                          <span className="font-bold">Race {bet.race} - #{bet.number}</span>
                          <span className="bg-racing-blue-light text-white px-2 py-1 rounded text-sm">
                            ${bet.odds.toFixed(2)}
                          </span>
                        </div>
                        <p className="font-bold">{bet.name}</p>
                        <p className="text-sm text-gray-600 mb-2">{bet.analysis}</p>
                        <div className="flex justify-between">
                          <span className="text-sm bg-racing-gray-dark px-2 py-1 rounded">
                            Confidence: {bet.confidence}%
                          </span>
                          <span className="text-sm bg-racing-green text-white px-2 py-1 rounded">
                            {bet.stake}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {raceAnalysis.bestBets.value && (
                <div>
                  <h3 className="font-bold text-racing-blue mb-2">Value Selection</h3>
                  <div className="border border-racing-gray-darker rounded p-3">
                    <div className="flex justify-between mb-2">
                      <span className="font-bold">Race {raceAnalysis.bestBets.value.race} - #{raceAnalysis.bestBets.value.number}</span>
                      <span className="bg-racing-blue-light text-white px-2 py-1 rounded text-sm">
                        ${raceAnalysis.bestBets.value.odds.toFixed(2)}
                      </span>
                    </div>
                    <p className="font-bold">{raceAnalysis.bestBets.value.name}</p>
                    <p className="text-sm text-gray-600 mb-2">{raceAnalysis.bestBets.value.analysis}</p>
                    <div className="flex justify-between">
                      <span className="text-sm bg-racing-gray-dark px-2 py-1 rounded">
                        Confidence: {raceAnalysis.bestBets.value.confidence}%
                      </span>
                      <span className="text-sm bg-racing-green text-white px-2 py-1 rounded">
                        {raceAnalysis.bestBets.value.stake}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Current Race */}
        {currentRace && (
          <div className="bg-white rounded-lg shadow-card mb-6">
            <div className="bg-racing-blue text-white p-3 rounded-t-lg">
              <h2 className="text-lg font-bold">Race {currentRace.race_number} - {currentRace.race_name}</h2>
              <div className="flex flex-wrap gap-2 mt-1">
                <span className="bg-racing-blue-light px-2 py-0.5 rounded text-sm">{currentRace.distance}m</span>
                <span className="bg-racing-blue-light px-2 py-0.5 rounded text-sm">{currentRace.class}</span>
                {currentRace.race_time && (
                  <span className="bg-racing-blue-light px-2 py-0.5 rounded text-sm">
                    {new Date(currentRace.race_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </span>
                )}
              </div>
            </div>
            
            <div className="p-4">
              {/* Race Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <h3 className="font-bold mb-2">Race Details</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {currentRace.weight_type && (
                      <div>
                        <span className="text-sm text-gray-600">Weight Type:</span>
                        <p>{currentRace.weight_type}</p>
                      </div>
                    )}
                    {currentRace.prize_money && (
                      <div>
                        <span className="text-sm text-gray-600">Prize Money:</span>
                        <p>${currentRace.prize_money.toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <h3 className="font-bold mb-2">Restrictions</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {currentRace.age_restrictions && (
                      <div>
                        <span className="text-sm text-gray-600">Age:</span>
                        <p>{currentRace.age_restrictions}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Selections */}
              {currentRace.selections && (
                <div>
                  {/* Top Selections */}
                  {currentRace.selections.top && currentRace.selections.top.length > 0 && (
                    <div className="mb-6">
                      <h3 className="font-bold text-racing-blue mb-3">Top Selections</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {currentRace.selections.top.map((selection, index) => (
                          <div key={index} className="border border-racing-gray-darker rounded overflow-hidden">
                            <div className="bg-racing-gray-dark p-2 flex justify-between items-center">
                              <span className="font-bold">#{selection.number} {selection.horse_name}</span>
                              <div className="flex gap-2">
                                <span className="bg-racing-blue text-white px-2 py-0.5 rounded text-sm">
                                  ${selection.odds.toFixed(2)}
                                </span>
                                <span className="bg-racing-green text-white px-2 py-0.5 rounded text-sm">
                                  ${selection.assessedOdds.toFixed(2)}
                                </span>
                              </div>
                            </div>
                            <div className="p-3">
                              <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                                <div>
                                  <span className="text-gray-600">Jockey:</span>
                                  <p>{selection.jockey}</p>
                                </div>
                                <div>
                                  <span className="text-gray-600">Trainer:</span>
                                  <p>{selection.trainer}</p>
                                </div>
                                <div>
                                  <span className="text-gray-600">Weight:</span>
                                  <p>{selection.weight}kg</p>
                                </div>
                                <div>
                                  <span className="text-gray-600">Barrier:</span>
                                  <p>{selection.barrier}</p>
                                </div>
                              </div>
                              
                              {selection.analysis && (
                                <div className="mb-3">
                                  <p className="text-sm">{selection.analysis.summary}</p>
                                </div>
                              )}
                              
                              <div className="flex justify-between">
                                <span className="text-sm bg-racing-gray-dark px-2 py-1 rounded">
                                  Confidence: {selection.confidence}%
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Value Selections */}
                  {currentRace.selections.value && currentRace.selections.value.length > 0 && (
                    <div className="mb-6">
                      <h3 className="font-bold text-racing-blue mb-3">Value Selections</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {currentRace.selections.value.map((selection, index) => (
                          <div key={index} className="border border-racing-gray-darker rounded overflow-hidden">
                            <div className="bg-racing-gray-dark p-2 flex justify-between items-center">
                              <span className="font-bold">#{selection.number} {selection.horse_name}</span>
                              <div className="flex gap-2">
                                <span className="bg-racing-blue text-white px-2 py-0.5 rounded text-sm">
                                  ${selection.odds.toFixed(2)}
                                </span>
                                <span className="bg-racing-green text-white px-2 py-0.5 rounded text-sm">
                                  ${selection.assessedOdds.toFixed(2)}
                                </span>
                              </div>
                            </div>
                            <div className="p-3">
                              <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                                <div>
                                  <span className="text-gray-600">Jockey:</span>
                                  <p>{selection.jockey}</p>
                                </div>
                                <div>
                                  <span className="text-gray-600">Trainer:</span>
                                  <p>{selection.trainer}</p>
                                </div>
                                <div>
                                  <span className="text-gray-600">Weight:</span>
                                  <p>{selection.weight}kg</p>
                                </div>
                                <div>
                                  <span className="text-gray-600">Barrier:</span>
                                  <p>{selection.barrier}</p>
                                </div>
                              </div>
                              
                              {selection.analysis && (
                                <div className="mb-3">
                                  <p className="text-sm">{selection.analysis.summary}</p>
                                </div>
                              )}
                              
                              <div className="flex justify-between">
                                <span className="text-sm bg-racing-gray-dark px-2 py-1 rounded">
                                  Confidence: {selection.confidence}%
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Speed Map */}
                  {currentRace.selections.speed_map && (
                    <div className="mb-6">
                      <h3 className="font-bold text-racing-blue mb-2">Speed Map</h3>
                      <div className="bg-racing-gray p-3 rounded">
                        <p>{currentRace.selections.speed_map}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Suggested Bets */}
                  {currentRace.selections.suggested_bets && currentRace.selections.suggested_bets.length > 0 && (
                    <div>
                      <h3 className="font-bold text-racing-blue mb-2">Suggested Bets</h3>
                      <div className="bg-racing-gray p-3 rounded">
                        {currentRace.selections.suggested_bets.map((bet, index) => (
                          <div key={index} className="mb-1 last:mb-0">
                            <strong>{bet.type}:</strong> {bet.selection} - {bet.stake}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Quaddie Section */}
        {raceAnalysis?.quaddie && (
          <div className="bg-white rounded-lg shadow-card mb-6">
            <div className="bg-racing-blue text-white p-3 rounded-t-lg">
              <h2 className="text-lg font-bold">Quaddie Selections</h2>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {raceAnalysis.quaddie.legs.map((leg, index) => (
                  <div key={index} className="border border-racing-gray-darker rounded p-3">
                    <h3 className="font-bold mb-2">Leg {index + 1} - Race {leg.race}</h3>
                    <div className="flex flex-wrap gap-1">
                      {leg.selections.map(selection => (
                        <span key={selection} className="bg-racing-blue text-white px-2 py-1 rounded">
                          {selection}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-bold">Cost:</span> ${raceAnalysis.quaddie.cost.toFixed(2)}
                </div>
                {raceAnalysis.quaddie.comment && (
                  <div className="text-sm text-gray-600">
                    {raceAnalysis.quaddie.comment}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default RacePage;
